// «Вопрос по заметке» (README §2, §6): беседа, начатая из заметки, держит её
// «в контексте» — плашка «в контексте: [📄 имя ×]» над полем ввода, и путь
// заметки уходит агенту с первой репликой (сервер проверяет, что он внутри
// базы профиля). Состояние беседы (§13: ctxNote) — на время жизни вкладки
// и в localStorage, чтобы плашка пережила перезагрузку.
//
// Вход для раздела «Заметки»: `askAboutNote(path, title)`.

import { create } from 'zustand'
import { api } from '../../api'
import { describeError, useAppStore } from '../../store/appStore'

export interface CtxNote {
  path: string
  title: string
  /** путь уже ушёл агенту — второй раз не шлём */
  sent: boolean
}

const KEY = 'abx-cli-ctxnote'

/** Подсказка в поле ввода беседы «вопрос по заметке». */
export const NOTE_HINT = 'спросите о заметке: что в ней решено, что осталось открытым, что сделать дальше…'

function load(): Record<string, CtxNote> {
  try {
    const raw = localStorage.getItem(KEY)
    const data = raw ? (JSON.parse(raw) as unknown) : {}
    return data && typeof data === 'object' ? (data as Record<string, CtxNote>) : {}
  } catch {
    return {}
  }
}

function save(notes: Record<string, CtxNote>): void {
  try { localStorage.setItem(KEY, JSON.stringify(notes)) } catch { /* без памяти */ }
}

interface CtxState {
  notes: Record<string, CtxNote>
  setNote(sessionId: string, note: CtxNote | null): void
  markSent(sessionId: string): void
}

export const useCtxNote = create<CtxState>((set, get) => ({
  notes: load(),
  setNote(sessionId, note) {
    const notes = { ...get().notes }
    if (note) notes[sessionId] = note
    else delete notes[sessionId]
    save(notes)
    set({ notes })
  },
  markSent(sessionId) {
    const cur = get().notes[sessionId]
    if (!cur || cur.sent) return
    get().setNote(sessionId, { ...cur, sent: true })
  },
}))

/** Имя заметки из пути: «работа/план.md» → «план». */
export function noteTitle(path: string): string {
  const name = path.split('/').filter(Boolean).pop() ?? path
  return name.replace(/\.md$/i, '')
}

/** «Спросить в беседе»: новая сессия «вопрос по заметке» с заметкой в контексте. */
export async function askAboutNote(path: string, title?: string): Promise<string | null> {
  const app = useAppStore.getState()
  const name = title?.trim() || noteTitle(path)
  try {
    const session = await api.createSession({ type: 'agent', title: `вопрос по заметке: ${name}` })
    useCtxNote.getState().setNote(session.id, { path, title: name, sent: false })
    useAppStore.setState((s) => ({ sessionHints: { ...s.sessionHints, [session.id]: NOTE_HINT } }))
    app.upsertSession(session)
    useAppStore.getState().focusSession(session.id)
    return session.id
  } catch (error) {
    app.setNotice(describeError(error))
    return null
  }
}
